import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Briefcase, Clock, ShieldCheck, ChevronRight } from "lucide-react";
import { useInView } from "@/hooks"; // Correction : useAnimatedCounter n'est pas utilisé ici
import { liveStatsService, heroService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { getOptimizedImageUrl } from "@/lib/cloudinary";
import type { HeroSection as HeroType, LiveStats } from "@/types";

const iconMap: Record<string, React.ElementType> = {
  users: Users,
  briefcase: Briefcase,
  clock: Clock,
  "shield-check": ShieldCheck,
};

export function HeroSection() {
  const { ref, isInView } = useInView();
  const [hero, setHero] = useState<HeroType | null>(null);
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Utiliser le listener en temps réel
    const unsubscribe = heroService.subscribe((data) => {
      // Correction : On vérifie si data existe et on prend le premier élément
      console.log("Données du hero mises à jour :", data[0]);
      if (data && data.length > 0) {
        setHero(data[0]);
      }

      // On force le chargement à false dès qu'on a une réponse (même vide)
      setLoading(false);
    });
    
    liveStatsService.get().then(setStats).catch(() => setLoading(false));
    
    return () => unsubscribe();
  }, []);

  if (loading) {
    
    return (
      <section className="min-h-[90vh] bg-gradient-hero flex items-center justify-center">
        <div className="animate-pulse text-white/50"> Chargement...</div>
      </section>
    );
  }

  console.log("herro contenuent : ", hero);
  const heroBackground = getOptimizedImageUrl(hero?.backgroundImage, ["w_1800", "c_fill"]);
  console.log("URL optimisée Hero:", heroBackground);


  return (
    
    <section ref={ref} className="relative min-h-[90vh] bg-gradient-hero overflow-hidden">
      {heroBackground && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${heroBackground})` }}
        />
      )}

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm"
            >
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
               Disponible à Abidjan {/* — Lancement 2026 */}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
            >
              {hero?.title || "Trouvez un artisan de confiance en 5 minutes."}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl"
            >
              {hero?.subtitle || "Plombiers, électriciens, mécaniciens, peintres, menuisiers... disponibles près de chez vous à Abidjan."}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-white/60 text-sm max-w-lg"
            >
              {hero?.description || "Allo Artisan connecte instantanément les particuliers et les entreprises avec des artisans qualifiés, vérifiés et géolocalisés."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 pt-4 relative z-20"
            >
              <Button
                size="lg"
                rounded="xl"
                className="bg-white text-primary hover:bg-white/90 font-semibold group cursor-pointer"
                 onClick={(e) => {
                  e.preventDefault();
                  const link = hero?.ctaClientLink || "#services";
                  window.location.assign(link); // .assign est plus robuste que .href
                }}
              >
                {hero?.ctaClientText || "Je suis Client — Je trouve un artisan"}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                rounded="xl"
                variant="outline"
                className="border-2 border-white/40 text-white hover:bg-white/10 font-semibold cursor-pointer"
                onClick={(e) => {
                e.preventDefault();
                const link = hero?.ctaArtisanLink || "#contact";
                window.location.assign(link);
              }}
              >
                {hero?.ctaArtisanText || "Je suis Artisan — Je rejoins la plateforme"}
              </Button>
            </motion.div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="grid grid-cols-2 gap-4"
          >
            {(hero?.stats && Array.isArray(hero.stats) ? hero.stats : []).map((stat, index) => {
              const Icon = iconMap[stat?.icon ?? "" ] || Users;
              return (
                <motion.div
                  key={stat.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-colors"
                >
                  <Icon className="w-8 h-8 text-primary mb-3" />
                  <div className="text-3xl font-bold text-white">
                    {stat.value}{stat.suffix || ""}
                  </div>
                  <div className="text-white/60 text-sm mt-1">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}
